import styles from './GlobalSearch.module.scss';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import SearchBarAndIcon from './Components/SearchBarAndIcon/SearchBarAndIcon';
import useSearchResultFetcher, { GlobalSearchResult } from './useSearchResultFetcher';
import { plusPurpleCircle } from '../../assets/hostedassets';
import AddToWatchlistDropdown from '../../modules/Investor/Portfolio/components/AddToWatchlist/AddToWatchlistDropdown';
import { crossBtnGrey } from '../../assets/hostedassets';
import { createPortal } from 'react-dom';
import { sendBulkAddNotification } from '../../store/investor/apiHelpers';
import { Mixpanel } from '../../utils/mixpanel';
import { useAppSelector } from '../../app/hooks';
import { BULK_ADD, BULK_ADD_CONFIRM } from '../../utils/constants/mixpanelEvents/investorEvents';
import Button from '../_custom/Button/Button';

type GlobalSearchProps = {
    showSearch: boolean;
    setShowSearch: Dispatch<SetStateAction<boolean>>;
};

const GlobalSearch = (props: GlobalSearchProps) => {
    const { showSearch, setShowSearch } = props;
    const [isBulkOpen, setIsBulkOpen] = useState(false);
    const [bulkText, setBulkText] = useState('');
    const [bulkLoading, setBulkLoading] = useState(false);
    const bulkTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const setBulkTextareaRef = (el: HTMLTextAreaElement | null) => {
        bulkTextareaRef.current = el;
        if (el) {
            el.focus();
        }
    };
    const userName = useAppSelector((state) => state.user.userData?.userName);
    const userEmail = useAppSelector((state) => state.user.userData?.primaryEmail);
    const pnoId = useAppSelector((state) => state.user.userData?.investorOrgId);
    const pnoName = useAppSelector((state) => state.user.userData?.pnoName);

    const {
        setSearchText,
        searchText,
        searchResults,
        navigateOrAddLead,
        setSearchResults,
        fetchCompanyInfoForResult,
        handleAddNewFlow,
        searchLoading,
    } = useSearchResultFetcher();

    const handleSelectAlreadyAdded = async (result: GlobalSearchResult) => {
        const accessType = result.companyAccessType || '';
        const resultWithGroup = { ...result, companyAccessType: accessType };
        setShowSearch(false);
        navigateOrAddLead(result.orgId, accessType, resultWithGroup, searchText);
    };

    const handleSelectAddNew = async (result: GlobalSearchResult) => {
        const accessType = result.companyAccessType || '';
        const resultWithGroup = { ...result, companyAccessType: accessType };
        setShowSearch(false);
        await handleAddNewFlow({
            baseResult: resultWithGroup,
            accessType,
            searchTextValue: searchText,
            initialPrefillLoadingFields: { website: true, brand: true },
            overwriteIdentifierFromCompanyInfo: true,
        });
    };
    const trackBulkEvent = () => {
        Mixpanel.track(BULK_ADD, {
            User_Name: userName,
            User_Email: userEmail,
            PNO_ID: pnoId,
            PNO_Name: pnoName,
        });
    };

    const trackBulkConfirmEvent = () => {
        const count = bulkText.split(',').filter((value) => value.trim().length > 0).length;

        Mixpanel.track(BULK_ADD_CONFIRM, {
            User_Name: userName,
            User_Email: userEmail,
            PNO_ID: pnoId,
            PNO_Name: pnoName,
            Bulk_Input_Count: count,
        });
    };

    const handleBulkConfirm = async () => {
        try {
            setBulkLoading(true);

            await sendBulkAddNotification({
                userName: userName || '',
                userEmail: userEmail || '',
                pnoId: pnoId || '',
                pnoName: pnoName || '',
                bulkInputText: bulkText,
            });

            trackBulkConfirmEvent();
            setIsBulkOpen(false);
            setBulkText('');
        } catch (error) {
            console.error('Bulk notification failed', error);
        } finally {
            setBulkLoading(false);
        }
    };

    const handleFooterAddNew = async () => {
        setShowSearch(false);
        const syntheticResult = {
            orgId: null,
            orgName: searchText,
            websiteUrl: '',
        };
        await handleAddNewFlow({
            baseResult: syntheticResult,
            accessType: '',
            searchTextValue: searchText,
            initialPrefillLoadingFields: { identifier: true, website: true, brand: true },
            overwriteIdentifierFromCompanyInfo: true,
            isAddUsingSearchText: true,
        });
    };
    useEffect(() => {
        if (!showSearch) {
            setSearchText('');
            setSearchResults(null);
        }
    }, [showSearch]);

    return (
        <div style={{ position: 'relative' }}>
            {showSearch ? <div className={styles.SearchBackdrop} onClick={() => setShowSearch(false)} /> : null}
            <div
                className={`${styles.GlobalSearchWrapper} ${
                    showSearch ? styles.GlobalSearchWrapperExpanded : styles.GlobalSearchWrapperCompact
                }`}
            >
                <SearchBarAndIcon
                    setSearchText={setSearchText}
                    searchText={searchText}
                    showSearch={showSearch}
                    onCrossIconClick={() => setShowSearch(false)}
                    onSearchIconClick={() => setShowSearch(!showSearch)}
                    onBulkAddClick={() => {
                        trackBulkEvent();
                        setShowSearch(false);
                        setIsBulkOpen(true);
                    }}
                    searchLoading={searchLoading}
                />
            </div>

            <div style={{ position: 'relative', height: 'auto' }}>
                <AddToWatchlistDropdown
                    visible
                    searchText={searchText}
                    searchResults={searchResults}
                    wrapperClassName={styles.SearchResults}
                    onSelectAlreadyAdded={handleSelectAlreadyAdded}
                    onSelectAddNew={handleSelectAddNew}
                    footerVisible={searchText.length >= 3}
                    footerLabel={`Add using "${searchText}"`}
                    onFooterAddNew={handleFooterAddNew}
                />
                {isBulkOpen &&
                    createPortal(
                        <div className={styles.BulkBackdrop}>
                            <div className={styles.BulkModal} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.BulkHeader}>
                                    <h2 className={styles.BulkTitle}>Bulk Add</h2>

                                    <span className={styles.BulkClose} onClick={() => setIsBulkOpen(false)}>
                                        <img src={crossBtnGrey} alt="close" className={styles.BulkCloseIcon} />
                                    </span>
                                </div>
                                <div>
                                    {bulkText.trim() && (
                                        <div className={styles.BulkSubtitle}>Add Comma Separated Names, CIN, LLPIN, PAN or GSTIN.</div>
                                    )}
                                    <textarea
                                        ref={setBulkTextareaRef}
                                        className={`${styles.BulkTextarea} ${bulkText.trim() ? styles.BulkTextareaFilled : ''}`}
                                        value={bulkText}
                                        placeholder="Add Comma Separated Names, CIN, LLPIN, PAN or GSTIN"
                                        onChange={(e) => setBulkText(e.target.value)}
                                    />
                                </div>
                                <Button
                                    text="Confirm"
                                    disabled={!bulkText.trim()}
                                    loading={bulkLoading}
                                    onClick={handleBulkConfirm}
                                    variant="purple"
                                    size="wide"
                                    style={{ marginTop: '16px' }}
                                />
                            </div>
                        </div>,
                        document.body,
                    )}
            </div>
        </div>
    );
};

export default GlobalSearch;
